/**
 * The cap rule — the one place a recipe, blend or journal count is judged.
 *
 * **Two runtimes run this function, and they must agree.** The Durable Object
 * calls it for a signed-in account, where it is the authority: the client is
 * optimistic, the server decides, and a refusal comes back through the ordinary
 * reconciliation (`worker/workspace-do.ts`, `sync/plan-limit.ts`). A **guest**
 * workspace has no server at all — nothing it dispatches ever leaves the device
 * — so `store/workspace-context.tsx` installs this same function as the store's
 * dispatch guard while the identity is `null`. Without that, the sidebar's
 * "Free · 12 of 10 recipes" and the import dialog's promise to stop at the cap
 * were both saying something the app did not do.
 *
 * It lives beside `lib/plan.ts` rather than inside it because that module's
 * header promises no imports at all, and this one needs the command union to
 * read a payload. `plan.ts` remains the table of numbers; this is the rule that
 * reads them.
 *
 * ## What "a cap" means here
 *
 * The caps arrive as {@link PlanCaps} rather than being looked up, because the
 * two callers know different things: the object has an entitlement row and a
 * deployment's overrides, and the guest binding has the free tier. Two values
 * mean "do not enforce" — `null`, which is an object that has never been told a
 * plan and must not invent one, and anything at or above
 * {@link UNLIMITED_RECIPES}, which is how a paid tier travels as a number.
 *
 * Only a command that would *add* a document is ever refused, which is what
 * keeps an account at its cap fully usable on the documents it already has:
 * every edit, every delete, and an undo-of-delete restoring an id the workspace
 * still holds, all pass straight through.
 *
 * The recipe meter is `capRecipeCount` (D-CO4), not `state.recipes`' size: a
 * recipe this account shared still counts against it even though sharing moved
 * it out of `recipes` and into an owner reference, and a recipe shared *with*
 * this account never counts, whether or not the merged view has copied it back
 * into `recipes` for display. `sharedRecipe.leave`'s copy (D-CO7) is not one of
 * the commands `newRecipeIdOf` recognises as an addition, so it lands even over
 * the cap — the leaver keeps what they had, and the copy is an ordinary recipe
 * from then on.
 */

import type { Command, CommandError, WorkspaceState } from '@beerwright/commands';
import { capRecipeCount } from '@beerwright/commands';
import { planName, UNLIMITED_RECIPES } from './plan';

/**
 * The three meters, as the caller knows them.
 *
 * `null` is "this caller has no opinion" and enforces nothing — see the header.
 */
export interface PlanCaps {
  readonly recipeLimit: number | null;
  readonly blendLimit: number | null;
  readonly journalLimit: number | null;
}

/**
 * The id of the recipe a command would *add*, or `null`.
 *
 * A free function rather than a method because it is a pure question about a
 * command, and because the answer is the whole of the entitlement rule: no
 * other command in the 52 can grow the recipe table.
 */
export function newRecipeIdOf(state: WorkspaceState, command: Command): string | null {
  if (command.type === 'recipe.create') {
    const id = command.payload.recipeId;
    return state.recipes[id] === undefined ? id : null;
  }
  if (command.type === 'recipe.restore') {
    const id = command.payload.recipe.id;
    return state.recipes[id] === undefined ? id : null;
  }
  return null;
}

/**
 * The id of the blend a command would *add*, or `null`.
 *
 * Same shape as {@link newRecipeIdOf}: create, and restore naming an id the
 * workspace does not already hold. Edits and undo-of-delete stay uncapped.
 */
export function newBlendIdOf(state: WorkspaceState, command: Command): string | null {
  if (command.type === 'blend.create') {
    const id = command.payload.blendId;
    return state.blends[id] === undefined ? id : null;
  }
  if (command.type === 'blend.restore') {
    const id = command.payload.blend.id;
    return state.blends[id] === undefined ? id : null;
  }
  return null;
}

/**
 * The id of the journal a command would *add*, or `null`.
 *
 * The third of the same shape (D-J10). Adding a *block* to a journal that
 * already exists is an edit, so only `journal.create` and a `journal.restore`
 * naming an unheld id can grow the table — which is what keeps a capped account
 * fully usable on the journals it has.
 */
export function newJournalIdOf(state: WorkspaceState, command: Command): string | null {
  if (command.type === 'journal.create') {
    const id = command.payload.journalId;
    return state.journals[id] === undefined ? id : null;
  }
  if (command.type === 'journal.restore') {
    const id = command.payload.journal.id;
    return state.journals[id] === undefined ? id : null;
  }
  return null;
}

/**
 * The plan rejection for a command, or `null` if the plan has no opinion.
 *
 * Three meters, all checked against the state *this* command would see:
 *
 * - **Recipes.** `recipe.create`, and `recipe.restore` naming an id the
 *   workspace does not hold.
 * - **Blends.** The same pair for `blend.create` / `blend.restore`.
 * - **Journals.** The same pair again for `journal.create` /
 *   `journal.restore` (D-J10).
 *
 * Everything else passes straight through, which is what keeps a capped
 * account fully usable on the documents it already has. The rejection
 * carries the reducer's own `CommandError` shape, so the client's
 * reconciliation drops the optimistic command through the path it already
 * has and only the *presentation* is special (see `sync/plan-limit.ts`).
 */
export function planCapRejection(
  state: WorkspaceState,
  command: Command,
  caps: PlanCaps,
): CommandError | null {
  const { recipeLimit, blendLimit, journalLimit } = caps;
  if (recipeLimit !== null) {
    const newRecipeId = newRecipeIdOf(state, command);
    if (newRecipeId !== null && capRecipeCount(state) >= recipeLimit) {
      return rejection(
        `Your plan includes ${recipeLimit} recipes. ${planName('brewmaster')} and ${planName('beerwright')} are unlimited.`,
        command,
      );
    }
  }
  if (blendLimit !== null && blendLimit < UNLIMITED_RECIPES) {
    const newBlendId = newBlendIdOf(state, command);
    if (newBlendId !== null && Object.keys(state.blends).length >= blendLimit) {
      return rejection(
        `Your plan includes ${blendLimit} blends. ${planName('brewmaster')} and ${planName('beerwright')} are unlimited.`,
        command,
      );
    }
  }
  if (journalLimit !== null && journalLimit < UNLIMITED_RECIPES) {
    const newJournalId = newJournalIdOf(state, command);
    if (newJournalId !== null && Object.keys(state.journals).length >= journalLimit) {
      return rejection(
        `Your plan includes ${journalLimit} journals. ${planName('brewmaster')} and ${planName('beerwright')} are unlimited.`,
        command,
      );
    }
  }
  return null;
}

/** The reducer's error shape, so a refusal travels the path every refusal does. */
function rejection(message: string, command: Command): CommandError {
  return {
    code: 'plan-limit',
    message,
    commandId: command.commandId,
    commandType: command.type,
    issues: [],
    index: null,
  };
}
